import type { DataProvider, RaRecord, SortPayload } from 'ra-core'
import { HttpError, httpClient } from '../lib/httpClient'

const DEFAULT_PAGE_SIZE = 25

/**
 * Per-resource field conventions. Not finalized yet (migration guide §10) —
 * the real API doesn't have a uniform `id` field (e.g. `/sections` uses
 * `section_id`), and the field used as the pagination cursor varies by
 * resource. Centralized here so tuning it once the resource map is confirmed
 * is a one-place fix, instead of touching every call site.
 */
export interface ResourceConfig {
  /** API field holding the resource's unique identifier, mapped to ra-core's required `id`. */
  idField: string
  /** API field used as the pagination cursor (must be a monotonically increasing, indexed field). Only used when `paginationStyle` is `'cursor'`. */
  cursorField: string
  /**
   * `'cursor'` (default): `?after={cursorField value}&limit=`, project-wide
   * convention (migration guide §3.2). `'page'`: `?page={1-based}&limit=`,
   * for resources whose real API is page/offset-based with a 1-based page
   * number (e.g. `branches` — confirmed against the live API, not a guess).
   * `'offset'`: `?offset={0-based row index}&limit=`, for resources whose
   * real API takes a raw row offset instead of a page number (e.g.
   * `products` — confirmed in review, HATCH-35).
   */
  paginationStyle: 'cursor' | 'page' | 'offset'
  /**
   * `paginationStyle: 'cursor'` only: the query param the pagination cursor
   * is sent under. Defaults to `'after'` (`?after={cursorField value}`,
   * the project-wide convention). Set to `'before'` for a resource whose
   * real API instead paginates backward through a `before` param while
   * reserving `after` for an unrelated, constant, user-set filter (e.g.
   * `orders`' real `?after={range start}&before={cursor}` shape, confirmed
   * from the source export's query template — see the `orders` config
   * below). Whichever key this is, it's still round-tripped exactly like
   * the default: seeded from `filter[cursorParam]` on the first page (so a
   * user-set `before` filter value becomes the first page's cursor), then
   * overridden by `meta.cursor` (the previous page's `nextCursor`) on every
   * page after.
   */
  cursorParam?: string
  /**
   * `paginationStyle: 'cursor'` only: value to seed `cursorParam` with on
   * the very first page, when neither `meta.cursor` nor a matching `filter`
   * entry is set. Most cursor resources tolerate an absent cursor param on
   * the first request; some (e.g. `form-submissions`) instead require it on
   * every request, confirmed from the source export's query template
   * (`before={{ currentPage == 0 ? moment().format() : afterCursor }}`) —
   * `() => new Date().toISOString()` reproduces that `moment().format()`
   * first-page default.
   */
  cursorDefault?: () => string
  /**
   * Some endpoints require the id repeated in the PATCH body, not just the
   * URL (confirmed for `branches`). Per HATCH-79, this is actually a
   * backend-wide convention ("All patch requests require id to be set in
   * body and in most of the cases in the URL as well"), not a per-resource
   * quirk — default a new writable `RESOURCE_CONFIG` entry to `true` unless
   * you've confirmed the specific endpoint omits it.
   */
  includeIdInPatchBody: boolean
  /**
   * When true, `getMany` (used by `ReferenceField`/`ReferenceArrayField`/
   * `AutocompleteInput` to resolve id(s) to display record(s)) fetches the
   * whole resource once via a bare unfiltered `GET /{resource}` and resolves
   * every requested id from that in-memory result, instead of issuing one
   * `GET /{resource}/{id}` per id. Matches how the source Retool app's
   * `getBrands`/`getInsuranceProviders` queries actually work — plain,
   * unpaginated GETs with no `limit` — confirmed against the retool-exports,
   * not a guess. Intended for small reference/lookup resources; not a sane
   * default for a resource with an unbounded row count.
   */
  fetchAllForGetMany: boolean
  /**
   * When set, `getMany` issues one `GET /{listPath ?? resource}?{param}=id1&{param}=id2` —
   * the real API's own multi-id filter — instead of either `fetchAllForGetMany`'s
   * full-collection fetch or the generic per-id `getOne` fallback. For a
   * resource whose real list endpoint accepts the id field itself as a
   * repeated query param (confirmed for `categories`: `GET
   * /categories?category_id=` takes a real array, per the live API spec) —
   * this is `categories`' own `idField` sent this way, so a form rendering
   * many selected ids (a multiselect's `ReferenceArrayField`) does one
   * request instead of N sequential `getOne`s. Not a sane default for a
   * resource with no such multi-id filter; those keep the `getOne`-per-id
   * fallback.
   */
  getManyIdParam?: string
  /**
   * Maps a `filter`/`filterDefaultValues` field name to the actual API query
   * param it's sent under, for filters whose param name doesn't match the
   * field name — e.g. branches' `title` filter is a real, confirmed
   * free-text search, but the API takes it as `?query=`, not `?title=`.
   */
  filterParamMap: Record<string, string>
  /**
   * Filter keys whose array value must be sent as a repeated query param
   * (`?status=a&status=b`) rather than the default comma-joined single value
   * (`?status=a,b`) — confirmed against the live API for `events`/
   * `events-unconfirmed`'s `status` filter (HATCH-121 review): the endpoint's
   * `status` query param is a `List[StatusEnum]`, rejecting a single
   * comma-joined string as one invalid enum value instead of splitting it.
   */
  explodeArrayParams: string[]
  /**
   * Substitutes a default array value for a filter key whose current value
   * is unset/empty, keyed by the filter's *pre-`filterParamMap`* name — for
   * a filter whose "nothing selected" state must still mean "restrict to
   * this default set", not "no restriction at all" (e.g.
   * `bookings-dashboard-internal`'s per-tab status filter, HATCH-121 round
   * 7: clearing the confirmed tab's status multi-select must keep
   * requesting the confirmed tab's own in-scope statuses, not fall through
   * to every event of every status). Applied before `filterParamMap`
   * renames the key, so distinct source keys mapped to the same real param
   * (e.g. `confirmed_status`/`escalated_status`, both -> `status`) can carry
   * different defaults.
   */
  filterEmptyDefaults: Record<string, unknown[]>
  /**
   * `getList` sends a bare `GET /{resource}?{filter}` — the given filter
   * fields, but no `page`/`limit`/`after` pagination params — and treats the
   * whole response as the one and only page (`hasNextPage: false`). For a
   * resource whose real endpoint is always scoped down to a small, bounded
   * set by a required filter (e.g. `schedules`/`form-links`, each scoped to
   * one branch via `?branch_id=`), rather than one meant to be browsed page
   * by page. Unlike `fetchAllForGetMany`, the filter *is* sent server-side —
   * this isn't a fetch-everything-then-filter-client-side resource.
   */
  bareFilteredList: boolean
  /**
   * `fetchAllForGetMany` resources only: filter keys that, when set, are
   * sent as real `?key=value` query params on `getList` instead of being
   * matched client-side against the full fetched list — for a resource
   * whose real API *does* have a dedicated filtered endpoint, unlike the
   * plain lookup tables (`brands`/`insurance-providers`) `fetchAllForGetMany`
   * was written for. `faqs`' source export has both `getFAQs` (bare,
   * unpaginated `GET faqs`) and `getFilteredFAQs` (`GET
   * faqs?product_id=&persona_id=`, `queryDisabled` unless at least one of
   * `select1`/`select2` — the table's own product/persona filters — is set,
   * HATCH-141 review: "i don't see you adding product_id and persona_id to
   * API filter"), so `getList` picks whichever one of those two the real
   * app would have run: any filter key listed here with a real value skips
   * the full-fetch entirely and requests the API pre-filtered by just those
   * keys (any other, non-listed filter still matched client-side against
   * that smaller result); with none of them set, it falls back to
   * `fetchAllForGetMany`'s existing full-fetch-then-client-filter behavior.
   */
  apiFilterKeys?: string[]
  /**
   * Overrides the base path segment `create`/`delete` send to, when it
   * differs from the one `getList`/`getOne` read from — e.g. `form-links` is
   * read at `GET /form-links` but written at `POST`/`DELETE /admin/form-links`
   * (confirmed from the source export's `linkFormToBranch`/
   * `deleteFormLinkFromBranch` queries). Defaults to the resource name.
   */
  mutateResourcePath?: string
  /**
   * Overrides `create`'s URL entirely, as a function of the submitted body —
   * for a resource whose real create endpoint nests a parent id in the path
   * (not just the body) at a base that `mutateResourcePath` can't express
   * because it's shared with `delete`'s own, differently-shaped path — e.g. a
   * cart-discount rule, created at `POST cart-discounts/{discount_id}/rules`
   * but deleted at `DELETE cart-discounts/rules/{rule_id}` (confirmed from
   * the source export's `createRule`/`deleteRule` queries,
   * `apps/cart-based-discounts`). The parent id is read off `data` itself
   * (the caller's `filter`/`createDefaults`, e.g. `EditableTable`'s
   * `filter={{ discount_id }}`, is already merged into the create body ahead
   * of this call), same precondition `nestedIn`'s `parentIdFilter` has for
   * reads.
   */
  createPath?: (data: Record<string, unknown>) => string
  /**
   * Overrides `update`'s URL for a resource whose real endpoint nests the id
   * under a second, per-call parent id in the path instead of the default
   * `/{resource}/{id}` — e.g. a day-schedule row, PATCHed at
   * `/schedules/{schedule_id}/day-schedule/{day_schedule_id}` (parent id
   * supplied by the caller via `params.meta`). Never reads `params.data` (so
   * the body stays exactly the caller's dirty fields, per the same
   * PATCH-semantics rule the rest of `update` follows).
   */
  updatePath?: (id: string, meta: unknown) => string
  /**
   * Overrides `updateMany`'s behavior for a resource whose real API exposes
   * one dedicated batch-update endpoint (a single PATCH covering every
   * selected id) instead of no batch endpoint at all — e.g. branches'
   * `PATCH /branches/hidden { branch_ids, hidden }` (HATCH-161 follow-up,
   * reviewer-supplied endpoint/body shape). When set, `updateMany` sends one
   * PATCH here with `{ [updateManyIdsField]: ids, ...data }`, instead of the
   * default fallback (one `update` PATCH per id, via `Promise.all` — see
   * `updateMany` below), which would 404 against a resource with no
   * per-id-batchable endpoint of its own.
   */
  updateManyPath?: string
  /**
   * `updateManyPath` only: the body key its id array is sent under — real
   * batch endpoints don't agree on one name (`branches/hidden`'s is
   * `branch_ids`, not the bare `ids` a generic REST convention might guess).
   * Defaults to `'ids'`.
   */
  updateManyIdsField?: string
  /**
   * Overrides `delete`'s URL for a resource whose real delete endpoint lives
   * at a different base path than `create` (`mutateResourcePath` can't
   * express this — it's shared by both). E.g. `users` (apps/users,
   * HATCH-100): `createUser` posts to the bare `users` path, but
   * `removeUser` deletes at `v2/admin/users/{id}` — confirmed from the
   * source export's own query definitions, not a guess.
   */
  deletePath?: (id: string) => string
  /**
   * Overrides `getOne`'s URL when a resource's real read and write endpoints
   * live at different base paths, instead of the default `/{resource}/{id}`
   * — e.g. `products`: the source export's `getProduct` reads
   * `v2/admin/products/{id}` (confirmed in review, HATCH-41 round 5 — this
   * is also the endpoint that returns each option's nested `provider_price`,
   * per the review's "it does fetch provider prices"), but `patchProductRaw`
   * writes plain `products/{id}` (no `v2/admin/` prefix, unconfirmed but left
   * as-is — see `apps/products/MIGRATION-NOTES.md`). Also used by the
   * `nestedIn` parent-record fetch below, since `options` (a product's
   * nested `options` array) needs that same real provider-price data.
   * Also used by `orders`, whose single-record endpoint (`admin/orders/{id}`)
   * lives at a different base path than its list endpoint (`orders/all/...`)
   * — confirmed from the source export's `getOrder` query,
   * `retool-exports/order-detail/queries.json`.
   */
  getOnePath?: (id: string) => string
  /**
   * For a resource with no per-id `GET` endpoint at all — only a filtered
   * list read (e.g. `specialists/participants`: the real API is `GET
   * /events/participants?participant_id=`, confirmed via `oaq.py`, not
   * `GET /events/participants/{id}`, which 404s — same shape
   * `marketplace-frontend`'s own `getAllParticipants({ participant_id })`
   * query uses, HATCH-356 review). `getOne` requests `listPath` (or the
   * resource name) with this filter key set to the requested id and takes
   * the first row of the (array) response, instead of the generic
   * `/{resource}/{id}` default. A skeleton addition per AGENTS.md's own
   * rule (a gap here belongs in the skeleton, not a per-resource hack) —
   * takes priority over `getOnePath` if both are set, though no resource
   * should need both.
   */
  getOneViaListFilter?: string
  /**
   * Overrides the collection-root path segment `getList` reads from and
   * `create` (when `mutateResourcePath` isn't set) posts to — not used by
   * `getOne`/`update`/`delete`, whose `/{id}` suffix means the trailing
   * slash this exists for doesn't apply there. Differs from the resource
   * name when e.g. `schedules`' real endpoint is `/schedules/` (trailing
   * slash); requesting the bare `/schedules` gets redirected there by the
   * API, and since that's a cross-origin redirect (the API is on a
   * different origin than this app), the browser blocks it as a CORS
   * violation before this code ever sees a response. Sending the real path
   * directly avoids the redirect rather than trying to make it work.
   * Defaults to the resource name.
   *
   * Also accepts a function of the (already `filterParamMap`-remapped)
   * `getList` filter, for a resource whose real endpoint takes a value as a
   * path segment instead of a query param (e.g. `address-directory-cities`:
   * `GET address-directory/{region_code}`, not `?region_code=`) — see
   * `pathFilterField` below for keeping that same value out of the query
   * string it'd otherwise also be sent in. Only consulted by `getList`
   * (`create`/`getMany`'s own `listPath` fallback have no per-call filter to
   * pass, so a function value is ignored there and falls back to the
   * resource name).
   */
  listPath?: string | ((filter: Record<string, unknown>) => string)
  /**
   * `bareFilteredList` + a function `listPath` only: a filter key that's
   * already baked into the dynamic path `listPath` builds, so it's dropped
   * from the query string instead of being sent twice (e.g. cities'
   * `region_code`, consumed into `address-directory/{region_code}`, isn't
   * also sent as `?region_code=`).
   */
  pathFilterField?: string
  /**
   * For a pseudo-resource with no listing endpoint of its own — it only ever
   * arrives nested inside a parent record (e.g. a product's `options` array,
   * HATCH-41 review round 2: "options loads from product.options — there's
   * no separate API for fetching options only") — `getList` fetches the
   * parent record instead and returns its nested array field, same
   * `withRaId`-mapped shape as any other list. Requires a filter carrying the
   * parent's id (e.g. `EditableTable`'s `filter={{ product_id }}`), same
   * precondition `bareFilteredList` has. Create/update/delete are unaffected
   * — those go through this resource's own real endpoints
   * (`mutateResourcePath`/plain `/{resource}/{id}`), only the listing itself
   * is nested.
   */
  nestedIn?: {
    /** The parent resource name, fetched via `getOne` (e.g. `products`). */
    parentResource: string
    /** The filter key holding the parent record's id (e.g. `product_id`). */
    parentIdFilter: string
    /** The parent record's field holding this resource's rows (e.g. `options`). */
    field: string
  }
  /**
   * Computes extra flat, top-level display fields from each fetched
   * `nestedIn` row, merged onto it before it reaches `ResourceTable`/
   * `EditableTable` — for a resource whose real API nests data
   * (`options`' own `provider_price.amount`/`.recommended_price`/
   * `.provider.rate`/`.provider.pricing_model`, HATCH-41 review round 4:
   * "Provider Price"/"Target Price"/"Provider Pricing" columns) that
   * `FieldConfig.name` can't address directly — react-hook-form treats a
   * dotted field name as a real nested path, not a literal key, which breaks
   * `EditableTable`'s flat dirty-field diffing (`pickDirtyFields`). Only
   * applied in the `nestedIn` branch below (its one current use), not every
   * `getList` path.
   */
  flattenRow?: (row: Record<string, unknown>) => Record<string, unknown>
  /**
   * The query param names a `meta.range` (calendar-style) `getList` call
   * sends the visible window under. Defaults to `{ start: 'start', end:
   * 'end' }`. Override for a resource whose real endpoint uses different
   * names — e.g. `events`/`holiday-config` (calendar app, migration guide
   * §7.2): confirmed from the source exports' `getUserEvents`/
   * `getHolidayConfig` queries, both `?after=&before=`, the same
   * range-as-`after`/`before` convention `orders`' cursor pagination
   * documents above, not a guess.
   */
  rangeParams?: { start: string; end: string }
}

export const DEFAULT_RESOURCE_CONFIG: ResourceConfig = {
  idField: 'id',
  cursorField: 'created_at',
  paginationStyle: 'cursor',
  includeIdInPatchBody: false,
  fetchAllForGetMany: false,
  filterParamMap: {},
  explodeArrayParams: [],
  filterEmptyDefaults: {},
  bareFilteredList: false,
}

/** A visible date-window query for calendar-style resources — bypasses cursor pagination entirely. */
export interface RangeQuery {
  /** ISO datetime (UTC), inclusive lower bound. */
  start: string
  /** ISO datetime (UTC), upper bound. */
  end: string
}

export interface GetListMeta {
  /** Opaque cursor from a previous page's `meta.nextCursor` result. Omit to fetch the first page. */
  cursor?: string
  /** When set, fetches a date range instead of a cursor page (calendar-style resources). */
  range?: RangeQuery
}

/**
 * The REST/pagination/PATCH-id/filter-remap engine shared by every app on
 * this backend (`docs/library-extraction.md` §6) — `resourceConfig` (the
 * per-resource `RESOURCE_CONFIG` map) is supplied by the caller, since it's
 * app-specific (each app's own resources), while everything below is not.
 */
export function createDataProvider(resourceConfig: Partial<Record<string, ResourceConfig>>): DataProvider {
  function configFor(resource: string): ResourceConfig {
    return resourceConfig[resource] ?? DEFAULT_RESOURCE_CONFIG
  }

  /**
   * `getMany`/`create`'s own `listPath ?? resource` fallback, for call sites
   * with no per-call filter to feed a function `listPath` (see
   * `ResourceConfig.listPath`) — a function value is meaningless without one,
   * so it's treated the same as unset here.
   */
  function staticListPath(listPath: ResourceConfig['listPath']): string | undefined {
    return typeof listPath === 'string' ? listPath : undefined
  }

  /**
   * Short-lived cache for a `fetchAllForGetMany` resource's own bare fetch
   * (`GET /{path}`, no query string — the request never varies with the
   * caller's filter, see the `fetchAllForGetMany` branches of `getList`/
   * `getMany` below). Without this, a search-as-you-type reference input
   * against one of these resources (e.g. `apps/products`' `brand_id` filter)
   * re-fetches the *entire* resource on every keystroke even though the
   * result is filtered client-side afterward and the request itself is
   * identical every time (HATCH-227 review: "why does brands input does query
   * each time i change text in input - while it does filtering on client?").
   * Keyed by path (distinct pseudo-resources sharing one real path, e.g.
   * `slim-products`/`addon-products`, get separate entries); a short TTL
   * (rather than caching forever) keeps a freshly created/updated/deleted row
   * from staying stale for the rest of the session — `invalidateBareListCache`
   * below also clears it eagerly right after any mutation on the resource.
   */
  const BARE_LIST_CACHE_TTL_MS = 30_000
  const bareListCache = new Map<string, { promise: Promise<unknown>; expiresAt: number }>()

  /**
   * Deliberately never forwards a caller's own `AbortSignal` — this fetch is
   * shared by however many concurrent callers happen to be mid-keystroke
   * against the same resource at once (that's the whole point of the cache),
   * so aborting it because *one* of those callers' inputs unmounted would
   * incorrectly fail every other caller still awaiting the same promise.
   */
  function fetchBareListCached(path: string): Promise<unknown> {
    const cached = bareListCache.get(path)
    const now = Date.now()
    if (cached && cached.expiresAt > now) return cached.promise
    const promise = httpClient<unknown>(`/${path}`).catch((error) => {
      bareListCache.delete(path)
      throw error
    })
    bareListCache.set(path, { promise, expiresAt: now + BARE_LIST_CACHE_TTL_MS })
    return promise
  }

  /** Drops any cached bare fetch for `resource`'s own real path — called after any successful mutation on a `fetchAllForGetMany` resource, so the next read reflects it immediately rather than waiting out `BARE_LIST_CACHE_TTL_MS`. */
  function invalidateBareListCache(resource: string): void {
    const { listPath } = configFor(resource)
    bareListCache.delete(staticListPath(listPath) ?? resource)
  }


  // Falls back to a plain `id` key when `idField` is absent — confirmed needed
  // for `dynamic-forms`' create response (HATCH-122 live-review round 2):
  // `getForm`/`getForms` return `form_id` like every other read, but
  // `createForm`'s response omits it and carries a plain `id` instead, so
  // `<Create>`'s post-submit redirect (`ra-core`'s `data.id`) had nothing to
  // navigate to. Harmless for every resource whose response already has
  // `idField` — this only ever fires when that key is missing.
  function withRaId<T extends Record<string, unknown>>(row: T, idField: string): T & RaRecord {
    return { ...row, id: (row[idField] ?? row.id) as RaRecord['id'] }
  }

  /**
   * The API returns bare JSON arrays for list endpoints today: no `items`
   * wrapper, no `next_cursor` field (migration guide §10 open item). Isolated
   * behind this function so a future envelope change is a one-place fix.
   */
  function parseListResponse<T>(raw: unknown): T[] {
    return raw as T[]
  }

  /** Renames filter keys to their real API query param, per `filterParamMap`; keys with no mapping pass through unchanged. */
  function remapFilterKeys(
    filter: Record<string, unknown>,
    filterParamMap: Record<string, string>,
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(filter).map(([key, value]) => [filterParamMap[key] ?? key, value]),
    )
  }

  /** Substitutes each `filterEmptyDefaults` key's default array when the filter's own value is unset/empty — see `ResourceConfig.filterEmptyDefaults`. */
  function applyFilterEmptyDefaults(
    filter: Record<string, unknown>,
    filterEmptyDefaults: Record<string, unknown[]>,
  ): Record<string, unknown> {
    if (Object.keys(filterEmptyDefaults).length === 0) return filter
    const result = { ...filter }
    for (const [key, defaultValue] of Object.entries(filterEmptyDefaults)) {
      const value = result[key]
      const isEmpty = value === undefined || value === null || (Array.isArray(value) && value.length === 0)
      if (isEmpty) result[key] = defaultValue
    }
    return result
  }

  /**
   * Drops any param with no real value — `undefined`/`null` (an unset
   * filter), `''` (a cleared text filter), `[]` (a multi-select filter with
   * nothing checked), or `false` (an unchecked boolean filter, e.g.
   * doctor-profiles' `include_not_approved`) — instead of sending e.g.
   * `?brand_id=` or `?include_not_approved=false`. An empty-but-set query
   * param isn't "no filter" to most APIs; it's "match brand_id = ''" (or
   * explicitly `false`), which isn't the same as omitting the param entirely.
   */
  function buildQuery(params: Record<string, unknown>, explodeArrayParams: string[] = []): string {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '' || value === false) continue
      if (Array.isArray(value)) {
        if (value.length === 0) continue
        if (explodeArrayParams.includes(key)) {
          for (const item of value) query.append(key, String(item))
        } else {
          query.set(key, value.join(','))
        }
        continue
      }
      query.set(key, String(value))
    }
    return query.toString()
  }

  /**
   * Appends a query string to a path that may already have its own hardcoded
   * `?query` baked in (e.g. `specialists-review`'s `listPath:
   * 'doctor-profiles?only_not_approved=true'`) — joins with `&` in that
   * case instead of a second `?`, which the real API rejects as a malformed
   * query string.
   */
  function withQuery(path: string, query: string): string {
    if (!query) return path
    return `${path}${path.includes('?') ? '&' : '?'}${query}`
  }

  /**
   * Client-side filtering for `fetchAllForGetMany` resources' `getList` — the
   * real API for these has no filter params to send server-side (a bare,
   * unpaginated `GET /{resource}` is all it supports), but `AutocompleteInput`/
   * `AutocompleteArrayInput` still search-as-you-type via `getList({ filter:
   * { q: searchText } })` (`DefaultFilterToQuery` in autocomplete-input.tsx),
   * so `q` needs to match *something* or typing in the combobox would never
   * narrow the (already fully in-memory) choice list. `q` matches against any
   * string field on the row (the data provider has no notion of which field
   * is the resource's `optionText`); every other filter key matches exactly,
   * or against any element for an array-valued filter (id-list style) — and,
   * for a row field that's itself an array (`tags`/`multi-enum`/a `multiple`
   * reference, e.g. `brands`' `brand_types` filter, HATCH-19 review), any
   * overlap between the selected values and the row's values counts as a match,
   * same "any of" semantics as the scalar-row case just applied on both sides.
   * A filter key with no real value — `undefined`/`null`/`''`, or `[]` (a
   * multi-select filter cleared back to nothing selected) — is treated as
   * unset and matches every row, the same "no filter" semantics `buildQuery`
   * already applies server-side; otherwise clearing e.g. `brands`'
   * `brand_types` filter would match zero rows instead of showing the
   * unfiltered list (HATCH-19 review). A scalar string field matches by
   * substring (case-insensitive), same as `q`, since there's no real
   * server-side search to fall back to for these resources — an exact match
   * would make a partial, as-you-type text filter effectively unusable
   * (HATCH-19 review, round 4).
   */
  function matchesFilter(row: Record<string, unknown>, filter: Record<string, unknown>): boolean {
    return Object.entries(filter).every(([key, value]) => {
      if (value === undefined || value === null || value === '') return true
      if (Array.isArray(value) && value.length === 0) return true
      if (key === 'q') {
        const needle = String(value).toLowerCase()
        return Object.values(row).some(
          (fieldValue) => typeof fieldValue === 'string' && fieldValue.toLowerCase().includes(needle),
        )
      }
      const rowValue = row[key]
      if (Array.isArray(value)) {
        if (Array.isArray(rowValue)) {
          const wanted = value.map(String)
          return rowValue.some((rowItem) => wanted.includes(String(rowItem)))
        }
        return value.map(String).includes(String(rowValue))
      }
      if (typeof rowValue === 'string' && typeof value === 'string') {
        return rowValue.toLowerCase().includes(value.toLowerCase())
      }
      return String(rowValue) === String(value)
    })
  }

  /**
   * `fetchAllForGetMany` resources only: the real API's own bare list endpoint
   * returns rows in whatever order it stores them, with no `?sort=` param to
   * ask for anything else — `useGetList`/`useListController` pass `sort`
   * straight through to the data provider and render its result verbatim
   * (ra-core does no client-side sorting of its own), so a caller like
   * `useSlimProducts`'s `sort: { field: 'title', order: 'ASC' }` would
   * otherwise silently have no effect. Sorts case-insensitively for a string
   * field (locale-aware, e.g. so casing doesn't reorder otherwise-alphabetical
   * titles) and numerically otherwise.
   */
  function sortRows(rows: Record<string, unknown>[], sort: SortPayload): void {
    const { field, order } = sort
    const direction = order === 'DESC' ? -1 : 1
    rows.sort((a, b) => {
      const left = a[field]
      const right = b[field]
      if (typeof left === 'string' && typeof right === 'string') {
        return left.localeCompare(right, undefined, { sensitivity: 'base' }) * direction
      }
      if (left === right) return 0
      return (String(left) < String(right) ? -1 : 1) * direction
    })
  }

  // `DataProvider`'s methods are individually generic over `RecordType`, which
  // defeats plain object-literal contextual typing (TS can't verify a concrete
  // return type against an arbitrary caller-supplied RecordType). `impl` is
  // typed concretely against ra-core's param/result shapes; the cast to
  // `DataProvider` below is the one place that bridges the two.
  const impl = {
    getList: async (resource: string, params: Parameters<DataProvider['getList']>[1]) => {
      const {
        idField,
        cursorField,
        cursorParam = 'after',
        cursorDefault,
        paginationStyle,
        filterParamMap,
        explodeArrayParams,
        filterEmptyDefaults,
        fetchAllForGetMany,
        apiFilterKeys,
        bareFilteredList,
        listPath,
        pathFilterField,
        nestedIn,
        flattenRow,
      } = configFor(resource)
      const meta = params.meta as GetListMeta | undefined
      const rawFilter = applyFilterEmptyDefaults(
        (params.filter ?? {}) as Record<string, unknown>,
        filterEmptyDefaults,
      )
      const filter = remapFilterKeys(rawFilter, filterParamMap)
      const path = typeof listPath === 'function' ? listPath(filter) : (listPath ?? resource)

      // A pseudo-resource with no listing endpoint of its own — fetch the
      // parent record instead and read its nested array field (see
      // `ResourceConfig.nestedIn`).
      if (nestedIn) {
        const parentId = rawFilter[nestedIn.parentIdFilter]
        const { getOnePath: parentGetOnePath } = configFor(nestedIn.parentResource)
        const parentPath = parentGetOnePath
          ? parentGetOnePath(String(parentId))
          : `/${nestedIn.parentResource}/${parentId}`
        const raw = await httpClient<Record<string, unknown>>(parentPath, {
          signal: params.signal,
        })
        const rows = (raw[nestedIn.field] as Record<string, unknown>[] | undefined) ?? []
        const data = rows.map((row) => withRaId(flattenRow ? { ...row, ...flattenRow(row) } : row, idField))
        return { data, pageInfo: { hasNextPage: false, hasPreviousPage: false } }
      }

      // Same "no real per-id/paginated endpoint" resource `getMany` already
      // special-cases (a small reference/lookup resource whose real API is a
      // single plain, unpaginated `GET /{resource}`) — `getList` needs the
      // same treatment, since `ReferenceInput`/`AutocompleteInput`'s choice
      // list is fetched via `getList`, not `getMany`. Without this, it fell
      // through to the generic paginated branches below and sent e.g.
      // `GET /brands?limit=26`, an endpoint that doesn't exist on the real API.
      if (fetchAllForGetMany) {
        // See `ResourceConfig.apiFilterKeys`: a real filter value on one of
        // these keys means the real app would run its dedicated filtered
        // endpoint (e.g. `faqs`' `getFilteredFAQs`) instead of the plain
        // fetch-everything one — request just that, server-side, rather than
        // pulling the whole resource to filter it client-side.
        const activeApiFilterKeys = apiFilterKeys?.filter((key) => {
          const value = rawFilter[key]
          return value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)
        })
        if (activeApiFilterKeys && activeApiFilterKeys.length > 0) {
          const apiFilter = Object.fromEntries(
            activeApiFilterKeys.map((key) => [filterParamMap[key] ?? key, rawFilter[key]]),
          )
          const raw = await httpClient<unknown>(`/${withQuery(path, buildQuery(apiFilter, explodeArrayParams))}`, {
            signal: params.signal,
          })
          const rows = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
          const remainingFilter = Object.fromEntries(
            Object.entries(rawFilter).filter(([key]) => !activeApiFilterKeys.includes(key)),
          )
          const data = rows.filter((row) => matchesFilter(row, remainingFilter))
          return { data, pageInfo: { hasNextPage: false, hasPreviousPage: false } }
        }

        const raw = await fetchBareListCached(path)
        const all = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
        // Same `pathFilterField` exclusion `bareFilteredList` below applies —
        // a filter key already baked into `path` (e.g. `address-directory-
        // cities`' `region_code`, a function `listPath`) has no matching
        // column on the fetched rows themselves (`AddressDirectoryEntry` is
        // just `{ code, name }`), so leaving it in `matchesFilter`'s filter
        // would reject every row instead of just scoping which cached fetch
        // ran.
        const clientFilter = pathFilterField
          ? Object.fromEntries(Object.entries(rawFilter).filter(([key]) => key !== pathFilterField))
          : rawFilter
        const data = all.filter((row) => matchesFilter(row, clientFilter))
        if (params.sort) sortRows(data, params.sort)
        // A filter keyed on `idField` itself with an array value (e.g.
        // `category-banners`' `banner_id: bannerIds`) means "this exact set,
        // in this exact order" rather than a plain match — reorder the
        // filtered rows to the caller's array order instead of the fetched
        // list's own order, overriding the `sort` ordering just applied above.
        const idOrder = rawFilter[idField]
        if (Array.isArray(idOrder)) {
          const order = idOrder.map(String)
          data.sort((a, b) => order.indexOf(String(a.id)) - order.indexOf(String(b.id)))
        }
        return { data, pageInfo: { hasNextPage: false, hasPreviousPage: false } }
      }

      // A resource always scoped down to a small, bounded set by a required
      // filter (e.g. one branch's schedules/form-links) rather than one meant
      // to be browsed page by page — see `ResourceConfig.bareFilteredList`.
      if (bareFilteredList) {
        const queryFilter = pathFilterField
          ? Object.fromEntries(Object.entries(filter).filter(([key]) => key !== pathFilterField))
          : filter
        const query = buildQuery(queryFilter, explodeArrayParams)
        const raw = await httpClient<unknown>(`/${withQuery(path, query)}`, { signal: params.signal })
        const data = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
        return { data, pageInfo: { hasNextPage: false, hasPreviousPage: false } }
      }

      if (meta?.range) {
        // Same `pathFilterField` exclusion `bareFilteredList` above applies —
        // needed the first time a calendar-style resource also bakes a filter
        // key into a function `listPath` (`specialist-events`' `owner_id`, a
        // path segment, not a query param) instead of a plain permanent
        // filter object like `holiday-config`'s `owner_id`/`branch_id`/`brand_id`.
        const rangeFilter = pathFilterField
          ? Object.fromEntries(Object.entries(filter).filter(([key]) => key !== pathFilterField))
          : filter
        const { start: startParam, end: endParam } = configFor(resource).rangeParams ?? { start: 'start', end: 'end' }
        const query = buildQuery(
          { ...rangeFilter, [startParam]: meta.range.start, [endParam]: meta.range.end },
          explodeArrayParams,
        )
        const raw = await httpClient<unknown>(`/${withQuery(path, query)}`, { signal: params.signal })
        const data = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
        return { data, pageInfo: { hasNextPage: false, hasPreviousPage: false } }
      }

      const perPage = params.pagination?.perPage ?? DEFAULT_PAGE_SIZE

      if (paginationStyle === 'page') {
        // `meta.cursor` doubles as an opaque "page token" here: the 1-based
        // page number as a string, round-tripped the same way the cursor
        // value is for `'cursor'`-style resources (see `CursorPagination`).
        const currentPage = meta?.cursor ? Number(meta.cursor) : 1
        // Request one extra row to detect a next page — the API has no
        // dedicated total-count field to check instead.
        const query = buildQuery({ ...filter, page: currentPage, limit: perPage + 1 }, explodeArrayParams)
        const raw = await httpClient<unknown>(`/${withQuery(path, query)}`, { signal: params.signal })
        const rows = parseListResponse<Record<string, unknown>>(raw)

        const hasNextPage = rows.length > perPage
        const page = hasNextPage ? rows.slice(0, perPage) : rows
        const data = page.map((row) => withRaId(row, idField))

        return {
          data,
          pageInfo: { hasNextPage, hasPreviousPage: currentPage > 1 },
          meta: { nextCursor: hasNextPage ? String(currentPage + 1) : undefined },
        }
      }

      if (paginationStyle === 'offset') {
        // `meta.cursor` doubles as an opaque page token here too, but holds
        // the raw 0-based row offset itself rather than a page number (see
        // `ResourceConfig.paginationStyle`).
        const currentOffset = meta?.cursor ? Number(meta.cursor) : 0
        // Request one extra row to detect a next page — the API has no
        // dedicated total-count field to check instead.
        const query = buildQuery({ ...filter, offset: currentOffset, limit: perPage + 1 }, explodeArrayParams)
        const raw = await httpClient<unknown>(`/${withQuery(path, query)}`, { signal: params.signal })
        const rows = parseListResponse<Record<string, unknown>>(raw)

        const hasNextPage = rows.length > perPage
        const page = hasNextPage ? rows.slice(0, perPage) : rows
        const data = page.map((row) => withRaId(row, idField))

        return {
          data,
          pageInfo: { hasNextPage, hasPreviousPage: currentOffset > 0 },
          meta: { nextCursor: hasNextPage ? String(currentOffset + perPage) : undefined },
        }
      }

      // Request one extra row to detect a next page — the API has no dedicated
      // `next_cursor` / total-count field to check instead. The cursor value
      // itself is seeded from the matching filter key on the first page (e.g.
      // orders' `before` filter, the date range's end) — see `cursorParam`.
      // Same `pathFilterField` exclusion `bareFilteredList`/`meta.range` above
      // apply — the first cursor-paginated resource whose real endpoint also
      // nests a parent id in the path via a function `listPath` (a
      // specialist's own `user_id`, `apps/specialists-portal`'s
      // `points-transactions`) rather than sending it as a query param.
      const cursorFilter = pathFilterField
        ? Object.fromEntries(Object.entries(filter).filter(([key]) => key !== pathFilterField))
        : filter
      const cursorValue = meta?.cursor ?? cursorFilter[cursorParam] ?? cursorDefault?.()
      const query = buildQuery({ ...cursorFilter, [cursorParam]: cursorValue, limit: perPage + 1 }, explodeArrayParams)
      const raw = await httpClient<unknown>(`/${withQuery(path, query)}`, { signal: params.signal })
      const rows = parseListResponse<Record<string, unknown>>(raw)

      const hasNextPage = rows.length > perPage
      const page = hasNextPage ? rows.slice(0, perPage) : rows
      const data = page.map((row) => withRaId(row, idField))
      const nextCursor = hasNextPage ? String(page[page.length - 1]?.[cursorField]) : undefined

      return {
        data,
        pageInfo: { hasNextPage, hasPreviousPage: Boolean(meta?.cursor) },
        meta: { nextCursor },
      }
    },

    getOne: async (resource: string, params: Parameters<DataProvider['getOne']>[1]) => {
      const { idField, getOnePath, getOneViaListFilter, listPath } = configFor(resource)
      if (getOneViaListFilter) {
        const query = buildQuery({ [getOneViaListFilter]: params.id })
        const raw = await httpClient<unknown>(`/${withQuery(staticListPath(listPath) ?? resource, query)}`, {
          signal: params.signal,
        })
        const [row] = parseListResponse<Record<string, unknown>>(raw)
        if (!row) throw new HttpError(404, undefined)
        return { data: withRaId(row, idField) }
      }
      const path = getOnePath ? getOnePath(String(params.id)) : `/${resource}/${params.id}`
      const raw = await httpClient<Record<string, unknown>>(path, {
        signal: params.signal,
      })
      return { data: withRaId(raw, idField) }
    },

    getMany: async (resource: string, params: Parameters<DataProvider['getMany']>[1]) => {
      const { idField, fetchAllForGetMany, listPath, getManyIdParam } = configFor(resource)
      if (fetchAllForGetMany) {
        // Same divergence `getList` already handles: a pseudo-resource's real
        // path (e.g. `doctor-users` -> `users/doctors`) can differ from its
        // `RESOURCE_CONFIG` key — `getMany` needs `listPath` too, not just a
        // bare `/${resource}` (apps/doctor-profiles, HATCH-38).
        const raw = await fetchBareListCached(staticListPath(listPath) ?? resource)
        const all = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
        const requestedIds = new Set(params.ids.map(String))
        return { data: all.filter((record) => requestedIds.has(String(record.id))) }
      }

      // A resource whose real list endpoint takes the id field as a repeated
      // multi-id filter (`ResourceConfig.getManyIdParam`) — one request for
      // every requested id, instead of the generic per-id `getOne` fallback
      // below (this ticket, HATCH-283: a categories multiselect resolving many
      // selected ids was firing N sequential requests).
      if (getManyIdParam) {
        const query = buildQuery({ [getManyIdParam]: params.ids }, [getManyIdParam])
        const raw = await httpClient<unknown>(`/${withQuery(staticListPath(listPath) ?? resource, query)}`, {
          signal: params.signal,
        })
        const requestedIds = new Set(params.ids.map(String))
        const all = parseListResponse<Record<string, unknown>>(raw).map((row) => withRaId(row, idField))
        return { data: all.filter((record) => requestedIds.has(String(record.id))) }
      }

      const results = await Promise.all(
        params.ids.map((id) => impl.getOne(resource, { id, meta: params.meta, signal: params.signal })),
      )
      return { data: results.map((result) => result.data) }
    },

    getManyReference: async (resource: string, params: Parameters<DataProvider['getManyReference']>[1]) => {
      const filter = (params.filter ?? {}) as Record<string, unknown>
      const result = await impl.getList(resource, {
        pagination: params.pagination,
        sort: params.sort,
        filter: { ...filter, [params.target]: params.id },
        meta: params.meta,
        signal: params.signal,
      })
      return result
    },

    create: async (resource: string, params: Parameters<DataProvider['create']>[1]) => {
      const { idField, mutateResourcePath, listPath, fetchAllForGetMany, createPath } = configFor(resource)
      const path = createPath
        ? createPath(params.data as Record<string, unknown>)
        : `/${mutateResourcePath ?? staticListPath(listPath) ?? resource}`
      const raw = await httpClient<Record<string, unknown>>(path, {
        method: 'POST',
        body: JSON.stringify(params.data),
      })
      let record = withRaId(raw, idField)
      // `withRaId`'s `idField`-then-`id` fallback (added HATCH-122 live-review
      // round 2) still isn't enough for a create response that omits *both* —
      // confirmed round 3: `dynamic-forms`' `createForm` response carries
      // neither `form_id` nor a plain `id`, so `<Create>`'s post-submit
      // redirect (`ra-core`'s `data.id`) still has nothing to navigate to, and
      // `useCreate` throws before the redirect ever runs ("Invalid dataProvider
      // response for create: missing id"). Rather than guess at the response
      // shape a third time, recover the real id from the one read this project
      // already confirms works: a `fetchAllForGetMany` resource's own bare list
      // (`getForms` does return `form_id` on every row) — matched against every
      // field just submitted so an earlier, differently-created row can't
      // false-positive. Scoped to `fetchAllForGetMany` resources only: that's
      // this project's existing "small enough to fetch wholesale" designation,
      // the only case where a follow-up unfiltered list read is cheap and safe.
      if ((record.id === undefined || record.id === null) && fetchAllForGetMany) {
        const raw2 = await httpClient<unknown>(`/${staticListPath(listPath) ?? resource}`)
        const submitted = params.data as Record<string, unknown>
        const match = parseListResponse<Record<string, unknown>>(raw2).find((row) =>
          Object.entries(submitted).every(([key, value]) => row[key] === value),
        )
        if (match) record = withRaId(match, idField)
      }
      invalidateBareListCache(resource)
      return { data: record }
    },

    update: async (resource: string, params: Parameters<DataProvider['update']>[1]) => {
      const { idField, includeIdInPatchBody, updatePath } = configFor(resource)
      // Send exactly the fields the caller passed in `data` — never merge with
      // `previousData`. Callers (e.g. EditableTable, §3.4) are responsible for
      // trimming `data` to dirty fields; this is a PATCH, not a PUT. Some APIs
      // (e.g. branches) additionally require the id repeated in the body.
      const body = includeIdInPatchBody ? { ...params.data, [idField]: params.id } : params.data
      const path = updatePath ? updatePath(String(params.id), params.meta) : `/${resource}/${params.id}`
      const raw = await httpClient<Record<string, unknown>>(path, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      invalidateBareListCache(resource)
      return { data: withRaId(raw, idField) }
    },

    updateMany: async (resource: string, params: Parameters<DataProvider['updateMany']>[1]) => {
      const { updateManyPath, updateManyIdsField } = configFor(resource)
      // A real dedicated batch-update endpoint (see `ResourceConfig.updateManyPath`)
      // — one PATCH covering every id, instead of the generic per-id fallback
      // below, which would 404 against a resource with no per-id-batchable
      // endpoint of its own.
      if (updateManyPath) {
        await httpClient<void>(`/${updateManyPath}`, {
          method: 'PATCH',
          body: JSON.stringify({ [updateManyIdsField ?? 'ids']: params.ids, ...params.data }),
        })
        invalidateBareListCache(resource)
        return { data: params.ids }
      }
      const ids = await Promise.all(
        params.ids.map(async (id) => {
          await impl.update(resource, {
            id,
            data: params.data,
            previousData: undefined as never,
            meta: params.meta,
          })
          return id
        }),
      )
      return { data: ids }
    },

    delete: async (resource: string, params: Parameters<DataProvider['delete']>[1]) => {
      const { mutateResourcePath, deletePath } = configFor(resource)
      const path = deletePath ? deletePath(String(params.id)) : `/${mutateResourcePath ?? resource}/${params.id}`
      await httpClient<void>(path, { method: 'DELETE' })
      invalidateBareListCache(resource)
      return { data: (params.previousData ?? { id: params.id }) as RaRecord }
    },

    deleteMany: async (resource: string, params: Parameters<DataProvider['deleteMany']>[1]) => {
      const ids = await Promise.all(
        params.ids.map(async (id) => {
          await impl.delete(resource, { id, meta: params.meta })
          return id
        }),
      )
      return { data: ids }
    },
  }

  return Object.assign(impl, { [RESET_BARE_LIST_CACHE]: () => bareListCache.clear() }) as DataProvider
}

const RESET_BARE_LIST_CACHE = Symbol('resetBareListCacheForTests')

/**
 * Test-only: clears a `createDataProvider` instance's `fetchAllForGetMany`
 * bare-list cache, so an earlier test's mocked response can't leak into a
 * later one requesting the same real path. `bareListCache` is deliberately
 * scoped to one `createDataProvider` call, not module-level (each app's own
 * instance has no shared runtime state with another app's, per
 * `docs/library-extraction.md` §6) — it outlives any one test's own mocked
 * `fetch`, hence needing an explicit reset between test cases.
 */
export function resetBareListCacheForTests(provider: DataProvider): void {
  ;(provider as unknown as Record<symbol, () => void>)[RESET_BARE_LIST_CACHE]?.()
}

